// =================================================================================================================
// Breakpoint Manager
// =================================================================================================================

export default class BreakpointManager {

    /**
     * Default Breakpoints.
     *
     * Uses Bootstrap v5.2 Breakpoints
     * @link https://getbootstrap.com/docs/4.6/layout/overview/#responsive-breakpoints
     *
     * */
    static breakpointTiers = {
        xs: 0,
        sm: 576,
        md: 768,
        lg: 992,
        xl: 1200,
        xxl: 1400,
    }


    /**
     * Breakpoint.
     *
     * Object containing the active breakpoint and the previous breakpoint
     * */
    static breakpoint = {
        active: '',
        previous: ''
    }


    /**
     * Event: Breakpoint Change.
     *
     * The custom event that is triggered when a breakpoint changes.
     *
     * */
    static breakpointChange = new Event('breakpointChange');


    /**
     * Constructor.
     *
     * Sets the default values and adds the event listeners.
     *
     * @param {object} breakpointTiers Override to add your own breakpoints. Syntax is breakpoint name then value as a number (no units)
     * @param {boolean} consoleLogging Set to true if you want to output breakpoints in the console
     *
     * */
    constructor(breakpointTiers, consoleLogging = false) {

        // Replace breakpoints with user breakpoint and sort them from smallest to largest
        if (breakpointTiers) {
            BreakpointManager.breakpointTiers = Object.fromEntries(
                Object.entries(breakpointTiers).sort(([,a],[,b]) => a-b)
            )
        }

        // Set the console log value
        BreakpointManager.consoleLogging = consoleLogging;

        // Get the breakpoint upon class instantiation
        BreakpointManager.setInitialBreakpoint()
        if (BreakpointManager.consoleLogging){
            console.log('Breakpoint: ' + BreakpointManager.breakpoint.active)
        }

        // Breakpoint change event listener
        document.addEventListener('breakpointChange', e => {
            if (BreakpointManager.consoleLogging){
                console.log('Breakpoint: ' + BreakpointManager.breakpoint.active)
            }
        });

        // Screen size event listener that fires the handle screen change function
        window.addEventListener('resize', BreakpointManager.handleScreenChange );

    }


    /**
     * Set the Initial Breakpoint.
     *
     * Used in the constructor to set the initial breakpoint
     */
    static setInitialBreakpoint(){
        let width = window.innerWidth;

        // Set the base breakpoint to the smallest one
        let breakpoint = Object.keys(BreakpointManager.breakpointTiers)[0];

        // Loop over the breakpoints object
        Object.entries(BreakpointManager.breakpointTiers).forEach(entry => {
            // Set up variables from entry
            let [breakpointName, breakpointWidth] = entry;

            // Run the loop
            if (width >= breakpointWidth) {
                breakpoint = breakpointName;
            }
        });

        // Break the loop and set active and previous breakpoint
        BreakpointManager.breakpoint.active = breakpoint;
    }


    /**
     * Handle Screen Size Change.
     *
     * Runs upon the changing of the screen size.
     * It uses the width of the window to set the current.
     * If the breakpoint has changed it will set the previous breakpoint and fire the breakpointChange event.
     * */
    static handleScreenChange() {
        let width = window.innerWidth;

        // Set the base breakpoint to the smallest one
        let breakpointActive = Object.keys(BreakpointManager.breakpointTiers)[0];
        let breakpointBeforeChange = BreakpointManager.breakpoint.active;

        // Loop over the breakpoints object
        Object.entries(BreakpointManager.breakpointTiers).forEach(entry => {
            // Set up variables from entry
            let [breakpointName, breakpointWidth] = entry;

            // Run the loop
            if (width >= breakpointWidth) {
                breakpointActive = breakpointName;
            }
        });

        // Break the loop and set active and previous breakpoint
        // If the breakpoint has changed, set the previous breakpoint and fire the event
        if (breakpointActive !== breakpointBeforeChange) {
            BreakpointManager.breakpoint.previous = breakpointBeforeChange;
            BreakpointManager.breakpoint.active = breakpointActive;
            document.dispatchEvent(BreakpointManager.breakpointChange);
        } else {
            BreakpointManager.breakpoint.active = breakpointActive;
        }

    }


    /**
     * Matches Specific Breakpoint.
     *
     * Checks to see if the current screen size matches the input breakpoint.
     *
     * @param {string} breakpoint the key of the breakpoint you are matching to in the breakpointTiers object
     *
     * @return {boolean}
     * */
    matchesBreakpointOnly(breakpoint){
        if (Object.keys(BreakpointManager.breakpointTiers).includes(breakpoint)) {
            let breakpointValue = BreakpointManager.breakpointTiers[breakpoint];
            let arrayOfValues = Object.values(BreakpointManager.breakpointTiers);
            let breakpointIndex = arrayOfValues.indexOf(breakpointValue);

            // If breakpoint is the largest breakpoint in breakpointTiers
            if (breakpointIndex + 1 === arrayOfValues.length) {
                return window.innerWidth >= breakpointValue;
            } else {
                // Return true is screen size under the next highest breakpoint
                return window.innerWidth >= breakpointValue && window.innerWidth < arrayOfValues[breakpointIndex + 1];
            }

        } else {
            return false
        }
    }


    /**
     * Matches Input Breakpoint and Up.
     *
     * Checks to see if the current screen size matches the input breakpoint or higher breakpoints.
     *
     * @param {string} breakpoint the key of the breakpoint you are matching to in the breakpointTiers object
     *
     * @return {boolean}
     * */
    matchesBreakpointsUp(breakpoint){
        if (Object.keys(BreakpointManager.breakpointTiers).includes(breakpoint)) {
            return BreakpointManager.breakpointTiers[breakpoint] <= window.innerWidth;
        } else {
            return false
        }
    }


    /**
     * Matches Input Breakpoint and Down.
     *
     * Checks to see if the current screen size is smaller than the input breakpoint or lower
     *
     * @param {string} breakpoint the key of the breakpoint you are matching to in the breakpointTiers object
     *
     * @return {boolean}
     * */
    matchesBreakpointsDown(breakpoint){
        if (Object.keys(BreakpointManager.breakpointTiers).includes(breakpoint)) {
            // We actually need the value of the next highest breakpoint
            let inputBreakpointValue = BreakpointManager.breakpointTiers[breakpoint];
            let arrayOfValues = Object.values(BreakpointManager.breakpointTiers);
            let breakpointIndex = arrayOfValues.indexOf(inputBreakpointValue);

            // If breakpoint is the largest breakpoint in breakpointTiers, always return true
            if (breakpointIndex + 1 === arrayOfValues.length) {
                return true;
            } else {
                // Return true is screen size under the next highest breakpoint
                return arrayOfValues[breakpointIndex + 1] > window.innerWidth;
            }

        } else {
            return false
        }
    }


    /**
     * Matches Input Breakpoint Between Two Sizes
     *
     * Checks to see if the current screen size is smaller matches the input breakpoint or lower
     *
     * @param {string} breakpointLower the key of the lower breakpoint to match in breakpointTiers object
     * @param {string} breakpointHigher the key of the upper breakpoint to match in breakpointTiers object
     *
     * @return {boolean}
     * */
    matchesBreakpointsBetween(breakpointLower, breakpointHigher){
        let arrayOfKeys = Object.keys(BreakpointManager.breakpointTiers);
        // Only run if both breakpoints are in breakpointTiers
        if (arrayOfKeys.includes(breakpointLower) && arrayOfKeys.includes(breakpointHigher)) {
            let breakpointLowerValue = BreakpointManager.breakpointTiers[breakpointLower];
            let breakpointHigherValue = BreakpointManager.breakpointTiers[breakpointHigher];
            let arrayOfValues = Object.values(BreakpointManager.breakpointTiers);
            let breakpointHigherIndex = arrayOfValues.indexOf(breakpointHigherValue);

            // If breakpoint is the largest breakpoint in breakpointTiers
            if (breakpointHigherIndex + 1 === arrayOfValues.length) {
                return breakpointLowerValue <= window.innerWidth;
            } else {
                // Return true is screen size under the next highest breakpoint
                return breakpointLowerValue <= window.innerWidth && arrayOfValues[breakpointHigherIndex + 1] > window.innerWidth;
            }
        } else {
            return false
        }
    }

}
